import React from "react";
import { Award, Headphones, ShieldCheck, Wallet } from "lucide-react";
import { useTranslation } from "react-i18next";
import HeroSection from "../components/HeroSection";
import PackageCard from "../components/PackageCard";
import SectionHeading from "../components/SectionHeading";
import { allPackages } from "./Packages";
import { useNavigate } from "react-router-dom";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import { useBookingModal } from "../context/BookingModalContext";
import founderImage from "../assets/images/founder-kamlesh-sharma.jpg";

const samplePackages = allPackages.filter((p) => ["1", "2", "3", "4"].includes(p.id));

const whyUs = [
  { icon: Award, key: "since1994" },
  { icon: Wallet, key: "bestPrice" },
  { icon: Headphones, key: "support" },
  { icon: ShieldCheck, key: "safe" },
];

const Home: React.FC = () => {
  const { t } = useTranslation();
  const { open } = useBookingModal();
  const navigate = useNavigate();

  useDocumentMeta({
    title: t("home.metaTitle"),
    description: t("home.metaDescription"),
    path: "/",
  });

  return (
    <div className="bg-sand-50">
      <HeroSection onBook={open} />

      {/* Featured Packages */}
      <section className="max-w-7xl mx-auto px-6 py-20 sm:py-24">
        <SectionHeading
          eyebrow={t("home.featured.eyebrow")}
          title={t("home.featured.title")}
          subtitle={t("home.featured.subtitle")}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {samplePackages.map((pkg) => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              title={t(`packages.items.${pkg.id}.title`)}
              description={t(`packages.items.${pkg.id}.description`)}
              onBook={open}
            />
          ))}
        </div>
        <div className="text-center mt-12">
          <button
            onClick={() => navigate("/packages")}
            className="border-2 border-brand-600 text-brand-700 font-bold px-8 py-3 rounded-full hover:bg-brand-600 hover:text-white transition-all"
          >
            {t("home.featured.viewAll")}
          </button>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-navy-950 py-20 sm:py-24 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-brand-500/10 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-navy-500/20 blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <SectionHeading
            eyebrow={t("home.whyUs.eyebrow")}
            title={t("home.whyUs.title")}
            tone="light"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyUs.map(({ icon: Icon, key }) => (
              <div
                key={key}
                className="bg-white/5 border border-white/10 rounded-2xl p-7 text-center text-white hover:bg-white/10 hover:border-brand-500/40 transition-all hover:-translate-y-1"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-brand-600 to-brand-400 text-navy-950 mb-5">
                  <Icon size={26} strokeWidth={2} />
                </div>
                <h3 className="font-serif text-lg font-bold mb-2">
                  {t(`home.whyUs.items.${key}.title`)}
                </h3>
                <p className="text-navy-200 text-sm leading-relaxed">
                  {t(`home.whyUs.items.${key}.desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Founder */}
      <section className="max-w-7xl mx-auto px-6 py-20 sm:py-24">
        <SectionHeading eyebrow={t("home.founder.eyebrow")} title={t("home.founder.title")} />
        <div className="flex flex-col md:flex-row items-center gap-10 md:gap-14 max-w-4xl mx-auto">
          <img
            src={founderImage}
            alt="Kamlesh Sharma, Founder & CEO of Shree Matel Tourism"
            loading="lazy"
            className="w-56 h-56 sm:w-64 sm:h-64 rounded-2xl object-cover shadow-card shrink-0"
          />
          <div className="text-center md:text-left">
            <p className="text-lg sm:text-xl text-navy-900/70 leading-relaxed italic mb-6">
              {t("home.founder.quote")}
            </p>
            <div className="font-serif text-xl font-bold text-navy-900">{t("home.founder.name")}</div>
            <div className="text-brand-600 font-semibold text-sm uppercase tracking-wider mt-1">
              {t("home.founder.role")}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 px-6 bg-gradient-to-r from-brand-700 via-brand-600 to-brand-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-4 text-white">
            {t("home.cta.title")}
          </h2>
          <p className="text-lg text-brand-100 mb-9">
            {t("home.cta.subtitle")}
          </p>
          <button
            onClick={() => open({ title: t("packages.cta.customTitle") })}
            className="bg-navy-950 text-brand-300 font-bold px-10 py-4 rounded-full hover:shadow-2xl hover:-translate-y-0.5 transition-all text-lg shadow-xl"
          >
            {t("home.cta.button")}
          </button>
        </div>
      </section>
    </div>
  );
};

export default Home;
